import { FC, Fragment } from 'react'
import { ContentList, ContentListItem, Heading, HeadingLevel, HeadingTag, IconButton, IconButtonFunction, IconName } from '@enbw/ui-react'
import { useTranslation } from 'react-i18next'
import styles from './styles.module.scss'
import { formatDate, FormattedDateMode } from '@/util/date-util'
import { valueOrEmpty } from '@/util/string-util'
import { CustomerMasterData } from '@/domain/contract/types/customer'

type FlyoutItem = {
  label: string
  value: string
  fullRow?: boolean
}

type FlyoutSection = {
  title: string
  items: FlyoutItem[]
}

type ContractDetailsFlyoutProps = {
  customer: CustomerMasterData
  onClose: () => void
}

export const ContractDetailsFlyout: FC<ContractDetailsFlyoutProps> = ({ customer, onClose }) => {
  const { t } = useTranslation()

  const sections: FlyoutSection[] = [
    {
      title: t('contract.customerFlyout.keyFigures.title'),
      items: [
        { label: t('contract.customerFlyout.keyFigures.fields.mobilityPlusId'), value: valueOrEmpty(customer.keyFigures.mobilityPlusId), fullRow: true },
        { label: t('contract.customerFlyout.keyFigures.fields.authSubject'), value: valueOrEmpty(customer.keyFigures.authSubject), fullRow: true },
        { label: t('contract.customerFlyout.keyFigures.fields.mekEmailAddress'), value: valueOrEmpty(customer.keyFigures.mekEmailAddress), fullRow: true }
      ]
    },
    {
      title: t('contract.customerFlyout.contracts.title'),
      items: [
        { label: t('contract.customerFlyout.contracts.fields.activeContracts'), value: customer.contractCounts.activeContracts.toString() },
        { label: t('contract.customerFlyout.contracts.fields.contractsInTermination'), value: customer.contractCounts.contractsInTermination.toString() },
        { label: t('contract.customerFlyout.contracts.fields.terminatedContracts'), value: customer.contractCounts.terminatedContracts.toString() }
      ]
    },
    {
      title: t('contract.customerFlyout.fleetMemberships.title'),
      items: [
        { label: t('contract.customerFlyout.fleetMemberships.fields.activeMemberships'), value: customer.fleetMembershipCounts.activeMemberships.toString() },
        {
          label: t('contract.customerFlyout.fleetMemberships.fields.previousMemberships'),
          value: customer.fleetMembershipCounts.previousMemberships.toString()
        }
      ]
    },
    {
      title: t('contract.customerFlyout.deviceInfo.title'),
      items: [
        { label: t('contract.customerFlyout.deviceInfo.fields.createdAt'), value: formatDate(customer.deviceData.createdAt, FormattedDateMode.DateAndTime) },
        { label: t('contract.customerFlyout.deviceInfo.fields.lastUsedAt'), value: formatDate(customer.deviceData.lastUsedAt, FormattedDateMode.DateAndTime) },
        { label: t('contract.customerFlyout.deviceInfo.fields.osName'), value: valueOrEmpty(customer.deviceData.osName) },
        { label: t('contract.customerFlyout.deviceInfo.fields.osVersion'), value: valueOrEmpty(customer.deviceData.osVersion) },
        { label: t('contract.customerFlyout.deviceInfo.fields.deviceName'), value: valueOrEmpty(customer.deviceData.deviceName) },
        { label: t('contract.customerFlyout.deviceInfo.fields.appVersion'), value: valueOrEmpty(customer.deviceData.appVersion) }
      ]
    }
  ]

  return (
    <div className={styles.flyoutCard}>
      <div className={styles.flyoutHeader}>
        <Heading level={HeadingLevel.H3} tag={HeadingTag.H3}>
          {t('contract.customerFlyout.title')}
        </Heading>

        <IconButton name={IconName.CloseLarge} function={IconButtonFunction.Tertiary} onClick={onClose}></IconButton>
      </div>

      <div className={styles.flyoutContent}>
        <div className={styles.flyoutSections}>
          {sections.map((section, index) => (
            <Fragment key={section.title}>
              <section className={styles.flyoutSection}>
                <Heading level={HeadingLevel.H4} tag={HeadingTag.H3}>
                  {section.title}
                </Heading>

                <ContentList>
                  <div className={styles.flyoutItemsGrid}>
                    {section.items.map((item) => (
                      <div key={`${section.title}-${item.label}`} className={item.fullRow ? styles.flyoutItemFullRow : undefined}>
                        <ContentListItem label={item.label} body={item.value} />
                      </div>
                    ))}
                  </div>
                </ContentList>
              </section>

              {index < sections.length - 1 && <div className={styles.flyoutSectionDivider} />}
            </Fragment>
          ))}
        </div>
      </div>
    </div>
  )
}
